import { useState } from 'react';
import PricingTables from './PricingTable';
import { useNavigate } from 'react-router-dom';
import { userAppContext } from '../../Context/Context';
import { useAcademicApplication } from '../../hooks/useAcademicApplication';
import { PricingCard } from './PricingCard';
import { UserContext } from '../../types';
import { PricingData } from './types';
import AcademicApplicationModal from '../Academic/AcademicApplicationModal';

const pricings: PricingData[] = [
  {
    title: 'Academic',
    price: 'Free',
    included: 'Access a curated subset of the data for research use.',
    description: 'Free for verified academic and non-profit institutions.',
  },
  {
    title: 'Standard',
    price: '1,199',
    priceMo: '99.92',
    included: 'Full dataset access through the web platform, including all metrics and annotations.',
    description: '**Ideal for**: users who need complete data without API access.',
  },
  {
    info: '✳ Most Popular ✳',
    title: 'Advanced',
    price: '1,999',
    priceMo: '166.58',
    included: 'Everything in Standard, plus full API access to integrate data into your tools and workflows.',
    description: '**Ideal for**: programmatic and high-volume workflows.',
  },
  {
    title: 'Premium',
    included: 'Enterprise access with full dataset downloads and integration into internal systems.',
    description: '**Ideal for**: large organizations with custom workflow needs.',
  },
];

function RenderPricings() {
  const navigate = useNavigate();
  const [showModalAcademic, setShowModalAcademic] = useState(false);
  const { user, isAuthenticated, groups } = userAppContext() as unknown as UserContext;
  const { submitAcademicApplication, academicStatus, trialType, userType, loadingStatus, activeCampaignLists } =
    useAcademicApplication(user?.email, isAuthenticated);

  const isTrial = groups?.includes('ROLE_TRIAL');
  const isStandardPlan = groups?.includes('ROLE_STANDARD');
  const isAcademicPlan = groups?.includes('ROLE_ACADEMIC');
  
  // Check if user can see prices (must be in lists 21 or 12)
  const canSeePrices =
    !isAuthenticated ||
    (activeCampaignLists && (activeCampaignLists.includes('21') || activeCampaignLists.includes('12')));
  
  // Users without pricing access are treated as commercial
  const shouldShowContactUs = 
    isAuthenticated && 
    (isStandardPlan || isAcademicPlan) && 
    !canSeePrices;
  
  const isCommercial = isTrial ? trialType === 'commercial' : (userType === 'commercial' || shouldShowContactUs);

  const scrollToPricingSection = () => {
    const pricingSection = document.getElementById('pricingSection');
    pricingSection?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleAcademicApply = () => {
    setShowModalAcademic(true);
  };

  if (isAuthenticated && loadingStatus) {
    return (
      <div className="PricingApp">
        <link rel="canonical" href="/plans" />
        <div className="app-container">
          <header>
            <h1 className="header-topic gradient-color" id="pricingSection">
              Our Plans
            </h1>
          </header>
          <div
            style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}
          >
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="PricingApp">
      <link rel="canonical" href="/plans" />
      <div className="app-container">
        <header>
          <h1 className="header-topic gradient-color" id="pricingSection">
            Our Plans
          </h1>
        </header>
        <div className="pricing-cards p-3" style={{ alignItems: 'start' }}>
          {pricings
            .filter(pricing => {
              if (!isAuthenticated) return true;

              // Hide Academic card for commercial users (including those without pricing access)
              if ((isCommercial || shouldShowContactUs) && pricing.title === 'Academic') return false;

              if (pricing.title === 'Premium' && isTrial && trialType === 'academic') return false;

              if (pricing.title === 'Premium' && !isCommercial && !isTrial && !shouldShowContactUs) return false;

              return true;
            })
            .map(pricing => (
              <PricingCard
                key={pricing.title}
                {...pricing}
                onAcademicApply={handleAcademicApply}
                isCommercial={isCommercial}
              />
            ))}
        </div>
        <div className="div-whats-included">
          <div className="pt-4">
            <PricingTables />
          </div>
        </div>
        {isCommercial && (
          <div className="flex justify-center text-center py-5 w-full">
            <div className="w-3/4 md:w-1/4">
              <button className="your-plan-btn" onClick={() => navigate('/Support')}>
                Contact Us
              </button>
            </div>
          </div>
        )}
        {!isCommercial && (
          <div className="flex justify-center text-center py-5 w-full">
            <div className="w-3/4 md:w-1/4">
              <button className="your-plan-btn" onClick={scrollToPricingSection}>
                FIND YOUR PERFECT PLAN
              </button>
            </div>
          </div>
        )}
      </div>

      {/* @ts-ignore */}
      <AcademicApplicationModal
        isOpen={showModalAcademic}
        onClose={() => setShowModalAcademic(false)}
        onSubmit={submitAcademicApplication}
        academicStatus={academicStatus}
      />
    </div>
  );
}

export default RenderPricings;
